<script>
import { fromApiDatetime, DISPLAY_DATETIME } from '@/util/apiDatetime';
import { formatNum } from '@/util/number';

export default {
  name: 'RobTable',
  props: {
    items: Array,
    loading: Boolean,
    error: Object,
    selectedAssetData: Object,
    confirmingDeleteId: [Number, String],
    pagination: {
      type: Object,
      default: () => ({
        current: 1,
        pageSize: 20,
        totalSize: 0,
      }),
    },
  },
  data() {
    return {
      robTableFields: [
        { key: 'mrobMass.massName', label: 'Asset' },
        { key: 'mrobStartTime', label: 'Start Time', formatter: 'formatDateToLocal' },
        { key: 'mrobEndTime', label: 'End Time', formatter: 'formatDateToLocal' },
        { key: 'mrobInitialValue', label: 'Initial Value (L)', formatter: (value) => this.formatNumber(value) },
        { key: 'mrobCreatedUserId', label: 'Created By' },
        { key: 'mrobCreatedTime', label: 'Created At', formatter: 'formatDateToLocal' },
        { key: 'actions', label: 'Actions' },
      ],
    };
  },
  methods: {
    formatNumber(value) {
      if (value === null || typeof value === 'undefined') return '';
      const numberValue = Number(value);
      if (Number.isNaN(numberValue)) return value;
      return formatNum(numberValue, 1);
    },
    formatDateToLocal(dateString) {
      if (!dateString) return '';
      return fromApiDatetime(dateString).format(DISPLAY_DATETIME);
    },
    handlePageChange(page) {
      this.$emit('page-changed', page);
    },
  },
};
</script>

<template>
  <div>
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h5 v-if="selectedAssetData" class="mb-0">ROB Profile {{ selectedAssetData.massName }}</h5>
      <b-button v-if="canCreate('master_rob_rob')" variant="primary" @click="$emit('add-new')">Add New ROB</b-button>
    </div>

    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" style="width: 3rem; height: 3rem;"></b-spinner>
      <p class="mt-3 text-muted">Loading ROB profiles...</p>
    </div>
    <div v-else-if="error" class="alert alert-danger d-flex align-items-center">
      <i class="ri-error-warning-line mr-2" style="font-size: 1.2rem;"></i>
      Failed to load data. Please try again.
    </div>
    <template v-else>
      <!-- Desktop: tabel biasa -->
      <div class="desktop-table">
        <b-table striped hover responsive :items="items" :fields="robTableFields" show-empty
          tbody-tr-class="rob-row-clickable"
          @row-clicked="$emit('view-details', $event)">
          <template #empty>
            <div class="text-center py-4">
              <i class="ri-file-list-3-line" style="font-size: 3rem; color: #dee2e6;"></i>
              <p class="text-muted mt-2 mb-0">No ROB profiles found.</p>
            </div>
          </template>
          <template #cell(actions)="data">
            <b-button variant="info" size="sm" class="mr-1" @click.stop="$emit('view-details', data.item)">Detail</b-button>
            <b-button v-if="canUpdate('master_rob_rob')" variant="warning" size="sm" class="mr-1"
              @click.stop="$emit('edit-rob', data.item)">Edit</b-button>
            <b-button v-if="canDelete('master_rob_rob')" size="sm"
              :variant="confirmingDeleteId === data.item.mrobId ? 'danger' : 'outline-danger'"
              @click.stop="$emit('delete-rob', data.item)" @mouseleave="$emit('update:confirmingDeleteId', null)">
              {{ confirmingDeleteId === data.item.mrobId ? 'Confirm?' : 'Delete' }}
            </b-button>
          </template>
        </b-table>
      </div>

      <!-- Mobile: card layout -->
      <div class="mobile-cards">
        <div v-if="!items || !items.length" class="text-center py-4">
          <i class="ri-file-list-3-line" style="font-size: 3rem; color: #dee2e6;"></i>
          <p class="text-muted mt-2 mb-0">No ROB profiles found.</p>
        </div>
        <div v-else class="rob-card-list">
          <div
            v-for="item in items"
            :key="item.mrobId"
            class="rob-card"
            @click="$emit('view-details', item)"
          >
            <div class="rob-card-header">
              <span class="rob-card-title">{{ item.mrobMass && item.mrobMass.massName || '-' }}</span>
              <i class="ri-arrow-right-s-line"></i>
            </div>
            <div class="rob-card-body">
              <div class="rob-card-row">
                <span class="rob-card-label">Start Time</span>
                <span class="rob-card-value">{{ formatDateToLocal(item.mrobStartTime) || '-' }}</span>
              </div>
              <div class="rob-card-row">
                <span class="rob-card-label">End Time</span>
                <span class="rob-card-value">{{ formatDateToLocal(item.mrobEndTime) || '-' }}</span>
              </div>
              <div class="rob-card-row">
                <span class="rob-card-label">Initial Value (L)</span>
                <span class="rob-card-value">{{ formatNumber(item.mrobInitialValue) || '-' }}</span>
              </div>
              <div class="rob-card-row">
                <span class="rob-card-label">Created By</span>
                <span class="rob-card-value">{{ item.mrobCreatedUserId || '-' }}</span>
              </div>
              <div class="rob-card-row">
                <span class="rob-card-label">Created At</span>
                <span class="rob-card-value">{{ formatDateToLocal(item.mrobCreatedTime) || '-' }}</span>
              </div>
            </div>
            <div class="rob-card-footer" @click.stop>
              <b-button variant="info" size="sm" @click="$emit('view-details', item)">Detail</b-button>
              <b-button v-if="canUpdate('master_rob_rob')" variant="warning" size="sm"
                @click="$emit('edit-rob', item)">Edit</b-button>
              <b-button v-if="canDelete('master_rob_rob')" size="sm"
                :variant="confirmingDeleteId === item.mrobId ? 'danger' : 'outline-danger'"
                @click="$emit('delete-rob', item)" @mouseleave="$emit('update:confirmingDeleteId', null)">
                {{ confirmingDeleteId === item.mrobId ? 'Confirm?' : 'Delete' }}
              </b-button>
            </div>
          </div>
        </div>
      </div>
    </template>

    <b-pagination
      v-if="items && items.length > 0"
      v-model="pagination.current"
      :per-page="pagination.pageSize"
      :total-rows="pagination.totalSize"
      @change="handlePageChange"
    ></b-pagination>
  </div>
</template>

<style scoped>
/* Show/hide per breakpoint */
.desktop-table { display: block; }
.mobile-cards  { display: none; }

@media (max-width: 767px) {
  .desktop-table { display: none; }
  .mobile-cards  { display: block; }
}

/* Desktop */
.rob-row-clickable {
  cursor: pointer;
}

/* Mobile card layout */
.rob-card-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 4px 0 12px;
}

.rob-card {
  border: 1px solid #dee2e6;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  cursor: pointer;
}

.rob-card-header {
  background: #343a40;
  color: #fff;
  padding: 10px 14px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.rob-card-title {
  font-weight: 600;
  font-size: 14px;
}

.rob-card-body {
  padding: 10px 14px;
}

.rob-card-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 5px 0;
  border-bottom: 1px solid #f0f0f0;
  gap: 8px;
}

.rob-card-row:last-child {
  border-bottom: none;
}

.rob-card-label {
  font-size: 12px;
  color: #6c757d;
  font-weight: 500;
  flex-shrink: 0;
  min-width: 110px;
}

.rob-card-value {
  font-size: 13px;
  color: #212529;
  text-align: right;
}

.rob-card-footer {
  padding: 8px 14px;
  background: #f8f9fa;
  border-top: 1px solid #dee2e6;
  display: flex;
  gap: 6px;
  justify-content: flex-end;
}
</style>
